/**
 * Client-side download helpers.
 *
 * Keep CSV/XML/PDF generation here so multiple pages (Archive, Recovery audit,
 * Spend, InvoiceDetail) can share the same primitives and we avoid duplicating
 * CSV escaping logic. PDFs are produced by opening the backend's printable
 * HTML in a new tab — there's no client-side PDF library in this bundle.
 */

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

/** Escape a value for inclusion in a CSV row. */
function csvCell(v: unknown): string {
  if (v == null) return '';
  const s = typeof v === 'string' ? v : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function toCsv(rows: Array<Record<string, unknown>>, columns: string[]): string {
  const header = columns.map(csvCell).join(',');
  const body = rows
    .map((r) => columns.map((c) => csvCell(r[c])).join(','))
    .join('\n');
  return `${header}\n${body}\n`;
}

export function saveCsv(filename: string, rows: Array<Record<string, unknown>>, columns: string[]): void {
  const csv = toCsv(rows, columns);
  // BOM so Excel auto-detects UTF-8.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  saveBlob(blob, filename);
}

/** Tax-Determination-Document CSV (one row per invoice line). */
export function buildTddCsv(invoice: {
  invoiceNumber: string;
  buyerTrn: string;
  buyerName: string;
  sellerTrn?: string;
  sellerName?: string;
  issueDate: string;
  currency: string;
  lineItems?: Array<{
    lineNumber?: number;
    description: string;
    quantity?: number | string;
    unitPriceMinor?: bigint | number | string;
    vatRate?: number | string;
    netMinor?: bigint | number | string;
    vatMinor?: bigint | number | string;
    totalMinor?: bigint | number | string;
  }>;
}): string {
  const minorToDecimal = (m: unknown): string => {
    if (m == null) return '0.00';
    const n = typeof m === 'bigint' ? Number(m) : typeof m === 'string' ? Number(m) : (m as number);
    return (n / 100).toFixed(2);
  };
  const rows = (invoice.lineItems ?? []).map((li) => ({
    invoice: invoice.invoiceNumber,
    issueDate: invoice.issueDate.slice(0, 10),
    sellerTrn: invoice.sellerTrn ?? '',
    sellerName: invoice.sellerName ?? '',
    buyerTrn: invoice.buyerTrn,
    buyerName: invoice.buyerName,
    line: li.lineNumber ?? '',
    description: li.description,
    quantity: li.quantity ?? '',
    unitPrice: minorToDecimal(li.unitPriceMinor),
    vatRate: li.vatRate ?? '',
    net: minorToDecimal(li.netMinor),
    vat: minorToDecimal(li.vatMinor),
    lineTotal: minorToDecimal(li.totalMinor),
    currency: invoice.currency,
  }));
  return toCsv(rows, [
    'invoice',
    'issueDate',
    'sellerTrn',
    'sellerName',
    'buyerTrn',
    'buyerName',
    'line',
    'description',
    'quantity',
    'unitPrice',
    'vatRate',
    'net',
    'vat',
    'lineTotal',
    'currency',
  ]);
}
